/**
 * Intelligent Retention Engine
 *
 * Manages automated retention scheduling, disposition workflows,
 * and archive transfer protocols for personnel file records.
 */

export interface RetentionPolicy {
  id: string;
  name: string;
  description: string;
  recordType: RecordType;
  activeRetentionYears: number;
  inactiveRetentionYears: number;
  dispositionAction: DispositionAction;
  legalBasis: string;
  reviewFrequencyMonths: number;
  notificationDaysBeforeDisposition: number;
}

export type RecordType =
  | 'personnel_file'
  | 'appointment_letter'
  | 'promotion_record'
  | 'transfer_record'
  | 'disciplinary_record'
  | 'leave_record'
  | 'training_record'
  | 'performance_evaluation'
  | 'personal_document'
  | 'correspondence';

export type DispositionAction =
  | 'archive_national'     // Transfer to National Archives
  | 'archive_mda'          // Retain at MDA archive
  | 'destroy_certified'    // Certified destruction
  | 'permanent_retention'  // Never dispose
  | 'review_required';     // Requires manual review

export type RetentionStatus =
  | 'active'               // Currently in active service
  | 'inactive'             // Staff separated, retention period active
  | 'pending_disposition'  // Retention expired, awaiting action
  | 'legal_hold'           // Exempt from disposition
  | 'archived'             // Transferred to archive
  | 'destroyed';           // Certified destroyed

export interface RetentionSchedule {
  pfileId: string;
  staffName: string;
  fileNumber: string;
  status: RetentionStatus;
  recordType: RecordType;
  policyId: string;
  policyName: string;
  createdAt: Date;
  separationDate?: Date;
  retentionEndDate?: Date;
  dispositionAction: DispositionAction;
  isOnLegalHold: boolean;
  legalHoldReason?: string;
  legalHoldStartDate?: Date;
  lastReviewDate?: Date;
  nextReviewDate?: Date;
  notifications: RetentionNotification[];
}

export interface RetentionNotification {
  id: string;
  type: 'pre_disposition' | 'review_due' | 'legal_hold_expiring' | 'action_required';
  message: string;
  dueDate: Date;
  isAcknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
}

export interface DispositionRecord {
  id: string;
  pfileId: string;
  fileNumber: string;
  staffName: string;
  action: DispositionAction;
  status: 'pending' | 'approved' | 'completed' | 'cancelled';
  requestedBy: string;
  requestedAt: Date;
  approvedBy?: string;
  approvedAt?: Date;
  completedBy?: string;
  completedAt?: Date;
  certificateNumber?: string;
  notes?: string;
  archiveLocation?: string;
  destructionMethod?: string;
  witnesses?: string[];
}

// Ghana Civil Service retention policies
const DEFAULT_RETENTION_POLICIES: RetentionPolicy[] = [
  {
    id: 'pol_personnel_file',
    name: 'Personnel File Retention',
    description: 'Standard retention for personnel file jackets',
    recordType: 'personnel_file',
    activeRetentionYears: 0, // Retain while in service
    inactiveRetentionYears: 7, // 7 years after separation
    dispositionAction: 'archive_national',
    legalBasis: 'Public Records and Archives Administration Act, 1997 (Act 535)',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 90,
  },
  {
    id: 'pol_appointment',
    name: 'Appointment Records',
    description: 'Letters of appointment, acceptance, and assumption of duty',
    recordType: 'appointment_letter',
    activeRetentionYears: 0,
    inactiveRetentionYears: 7,
    dispositionAction: 'archive_national',
    legalBasis: 'Civil Service Act, 1993 (PNDCL 327)',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 90,
  },
  {
    id: 'pol_promotion',
    name: 'Promotion Records',
    description: 'Promotion letters and grade advancement documentation',
    recordType: 'promotion_record',
    activeRetentionYears: 0,
    inactiveRetentionYears: 7,
    dispositionAction: 'archive_national',
    legalBasis: 'Civil Service Act, 1993 (PNDCL 327)',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 90,
  },
  {
    id: 'pol_transfer',
    name: 'Transfer Records',
    description: 'Transfer letters, posting orders, and secondment documents',
    recordType: 'transfer_record',
    activeRetentionYears: 0,
    inactiveRetentionYears: 5,
    dispositionAction: 'archive_mda',
    legalBasis: 'Civil Service Regulations',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 60,
  },
  {
    id: 'pol_disciplinary',
    name: 'Disciplinary Records',
    description: 'Disciplinary actions, queries, and related correspondence',
    recordType: 'disciplinary_record',
    activeRetentionYears: 0,
    inactiveRetentionYears: 10, // Longer retention for disciplinary
    dispositionAction: 'archive_national',
    legalBasis: 'Civil Service Act, 1993 (PNDCL 327)',
    reviewFrequencyMonths: 24,
    notificationDaysBeforeDisposition: 120,
  },
  {
    id: 'pol_leave',
    name: 'Leave Records',
    description: 'Leave applications, approvals, and balance records',
    recordType: 'leave_record',
    activeRetentionYears: 0,
    inactiveRetentionYears: 3,
    dispositionAction: 'destroy_certified',
    legalBasis: 'Civil Service Regulations',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 30,
  },
  {
    id: 'pol_training',
    name: 'Training Records',
    description: 'Training certificates, course completions, and CPD records',
    recordType: 'training_record',
    activeRetentionYears: 0,
    inactiveRetentionYears: 5,
    dispositionAction: 'archive_mda',
    legalBasis: 'Training and Development Policy',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 60,
  },
  {
    id: 'pol_performance',
    name: 'Performance Evaluations',
    description: 'Annual performance appraisals and reviews',
    recordType: 'performance_evaluation',
    activeRetentionYears: 0,
    inactiveRetentionYears: 5,
    dispositionAction: 'archive_mda',
    legalBasis: 'Performance Management Policy',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 60,
  },
  {
    id: 'pol_personal_docs',
    name: 'Personal Documents',
    description: 'Certificates, IDs, and personal identification documents',
    recordType: 'personal_document',
    activeRetentionYears: 0,
    inactiveRetentionYears: 7,
    dispositionAction: 'archive_national',
    legalBasis: 'Public Records and Archives Administration Act, 1997 (Act 535)',
    reviewFrequencyMonths: 12,
    notificationDaysBeforeDisposition: 90,
  },
];

class RetentionEngineService {
  private policies: Map<string, RetentionPolicy> = new Map();

  constructor() {
    // Initialize with default policies
    DEFAULT_RETENTION_POLICIES.forEach(policy => {
      this.policies.set(policy.id, policy);
    });
  }

  /**
   * Get all retention policies
   */
  getPolicies(): RetentionPolicy[] {
    return Array.from(this.policies.values());
  }

  /**
   * Get policy by ID
   */
  getPolicy(policyId: string): RetentionPolicy | undefined {
    return this.policies.get(policyId);
  }

  /**
   * Get policy for a record type
   */
  getPolicyForRecordType(recordType: RecordType): RetentionPolicy | undefined {
    return Array.from(this.policies.values()).find(p => p.recordType === recordType);
  }

  /**
   * Calculate retention schedule for a PFile
   */
  calculateRetentionSchedule(params: {
    pfileId: string;
    staffName: string;
    fileNumber: string;
    recordType: RecordType;
    createdAt: Date;
    separationDate?: Date;
    isOnLegalHold?: boolean;
    legalHoldReason?: string;
  }): RetentionSchedule {
    const policy = this.getPolicyForRecordType(params.recordType) || DEFAULT_RETENTION_POLICIES[0];

    // Calculate retention end date
    let retentionEndDate: Date | undefined;
    let status: RetentionStatus = 'active';

    if (params.separationDate) {
      // Staff has separated - calculate retention end
      retentionEndDate = new Date(params.separationDate);
      retentionEndDate.setFullYear(retentionEndDate.getFullYear() + policy.inactiveRetentionYears);

      const now = new Date();
      if (now >= retentionEndDate) {
        status = 'pending_disposition';
      } else {
        status = 'inactive';
      }
    }

    // Legal hold overrides normal status
    if (params.isOnLegalHold) {
      status = 'legal_hold';
    }

    // Calculate next review date
    const lastReviewDate = new Date();
    const nextReviewDate = new Date(lastReviewDate);
    nextReviewDate.setMonth(nextReviewDate.getMonth() + policy.reviewFrequencyMonths);

    // Generate notifications
    const notifications: RetentionNotification[] = [];

    if (status === 'pending_disposition') {
      notifications.push({
        id: `notif_disp_${params.pfileId}`,
        type: 'action_required',
        message: `File ${params.fileNumber} has reached retention end date and requires disposition action.`,
        dueDate: new Date(),
        isAcknowledged: false,
      });
    } else if (retentionEndDate) {
      const daysUntilDisposition = Math.ceil((retentionEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysUntilDisposition <= policy.notificationDaysBeforeDisposition && daysUntilDisposition > 0) {
        notifications.push({
          id: `notif_pre_disp_${params.pfileId}`,
          type: 'pre_disposition',
          message: `File ${params.fileNumber} will reach retention end in ${daysUntilDisposition} days.`,
          dueDate: retentionEndDate,
          isAcknowledged: false,
        });
      }
    }

    return {
      pfileId: params.pfileId,
      staffName: params.staffName,
      fileNumber: params.fileNumber,
      status,
      recordType: params.recordType,
      policyId: policy.id,
      policyName: policy.name,
      createdAt: params.createdAt,
      separationDate: params.separationDate,
      retentionEndDate,
      dispositionAction: policy.dispositionAction,
      isOnLegalHold: params.isOnLegalHold || false,
      legalHoldReason: params.legalHoldReason,
      legalHoldStartDate: params.isOnLegalHold ? new Date() : undefined,
      lastReviewDate,
      nextReviewDate,
      notifications,
    };
  }

  /**
   * Get records pending disposition
   */
  getRecordsPendingDisposition(schedules: RetentionSchedule[]): RetentionSchedule[] {
    return schedules.filter(s => s.status === 'pending_disposition' && !s.isOnLegalHold);
  }

  /**
   * Get records approaching disposition
   */
  getRecordsApproachingDisposition(schedules: RetentionSchedule[], daysThreshold: number = 90): RetentionSchedule[] {
    const now = new Date();
    return schedules.filter(s => {
      if (!s.retentionEndDate || s.status === 'pending_disposition' || s.isOnLegalHold) return false;
      const daysUntil = Math.ceil((s.retentionEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntil > 0 && daysUntil <= daysThreshold;
    });
  }

  /**
   * Get records on legal hold
   */
  getRecordsOnLegalHold(schedules: RetentionSchedule[]): RetentionSchedule[] {
    return schedules.filter(s => s.isOnLegalHold);
  }

  /**
   * Get retention summary statistics
   */
  getRetentionSummary(schedules: RetentionSchedule[]): {
    total: number;
    active: number;
    inactive: number;
    pendingDisposition: number;
    onLegalHold: number;
    archived: number;
    destroyed: number;
    approachingDisposition: number;
    byDispositionAction: Record<DispositionAction, number>;
  } {
    const summary = {
      total: schedules.length,
      active: 0,
      inactive: 0,
      pendingDisposition: 0,
      onLegalHold: 0,
      archived: 0,
      destroyed: 0,
      approachingDisposition: this.getRecordsApproachingDisposition(schedules).length,
      byDispositionAction: {
        archive_national: 0,
        archive_mda: 0,
        destroy_certified: 0,
        permanent_retention: 0,
        review_required: 0,
      } as Record<DispositionAction, number>,
    };

    schedules.forEach(s => {
      switch (s.status) {
        case 'active': summary.active++; break;
        case 'inactive': summary.inactive++; break;
        case 'pending_disposition': summary.pendingDisposition++; break;
        case 'legal_hold': summary.onLegalHold++; break;
        case 'archived': summary.archived++; break;
        case 'destroyed': summary.destroyed++; break;
      }
      summary.byDispositionAction[s.dispositionAction]++;
    });

    return summary;
  }

  /**
   * Create disposition request
   */
  createDispositionRequest(params: {
    pfileId: string;
    fileNumber: string;
    staffName: string;
    action: DispositionAction;
    requestedBy: string;
    notes?: string;
  }): DispositionRecord {
    return {
      id: `disp_${Date.now()}_${params.pfileId}`,
      pfileId: params.pfileId,
      fileNumber: params.fileNumber,
      staffName: params.staffName,
      action: params.action,
      status: 'pending',
      requestedBy: params.requestedBy,
      requestedAt: new Date(),
      notes: params.notes,
    };
  }

  /**
   * Get disposition action label
   */
  getDispositionActionLabel(action: DispositionAction): string {
    const labels: Record<DispositionAction, string> = {
      archive_national: 'Transfer to National Archives',
      archive_mda: 'Archive at MDA',
      destroy_certified: 'Certified Destruction',
      permanent_retention: 'Permanent Retention',
      review_required: 'Manual Review Required',
    };
    return labels[action];
  }

  /**
   * Get status label
   */
  getStatusLabel(status: RetentionStatus): string {
    const labels: Record<RetentionStatus, string> = {
      active: 'Active Service',
      inactive: 'Retention Period',
      pending_disposition: 'Pending Disposition',
      legal_hold: 'Legal Hold',
      archived: 'Archived',
      destroyed: 'Destroyed',
    };
    return labels[status];
  }
}

// Export singleton instance
export const retentionEngine = new RetentionEngineService();

export type { RetentionEngineService };
